// App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AdminDashboard from "./pages/AdminDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import InvitePage from "./pages/InvitePage";
import EmployeeTimeOffPage from "./pages/EmployeeTimeOffPage";
import AdminTimeOffDashboard from "./pages/AdminTimeOffDashboard";
import TimeEntryDisputesDashboard from "./pages/TimeEntryDisputesDashboard";
import AdminLocationSettingsPage from "./pages/AdminLocationSettingsPage";
// Import the new settings pages
import EmployeeSettingsPage from "./pages/EmployeeSettingsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
// Import the new team calendar and dashboard pages
import TeamCalendarPage from "./pages/TeamCalendarPage";

function App() {
    const [user, setUser] = useState(null);
    const [orgId, setOrgId] = useState(null);
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            console.log("👤 Auth State Changed:", firebaseUser);

            if (!firebaseUser) {
                setUser(null);
                setRole(null);
                setOrgId(null);
                setLoading(false);
                return;
            }

            setUser(firebaseUser);

            try {
                const userOrgRef = doc(db, "userOrgs", firebaseUser.uid);
                const userOrgSnap = await getDoc(userOrgRef);
                if (!userOrgSnap.exists()) throw new Error("Org link missing");

                const fetchedOrgId = userOrgSnap.data().orgId;
                setOrgId(fetchedOrgId);

                const userRef = doc(db, "organizations", fetchedOrgId, "users", firebaseUser.uid);
                const userSnap = await getDoc(userRef);
                if (!userSnap.exists()) throw new Error("User profile missing");

                const fetchedRole = userSnap.data().role;
                setRole(fetchedRole);

                console.log("✅ UID:", firebaseUser.uid);
                console.log("🏢 Org ID:", fetchedOrgId);
                console.log("🎭 Role:", fetchedRole);
            } catch (err) {
                console.error("❌ Error loading role:", err);
                setOrgId(null);
                setRole("unassigned");
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) return <p>Loading...</p>;

    // Debug logging for routing state
    console.log("🧭 Routing state:", {
        isAuthenticated: !!user,
        role,
        orgId,
        shouldGoToAdmin: user && role === "admin",
        shouldGoToEmployee: user && role === "employee"
    });

    return (
        <Router>
            <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/invite/:orgId/:email" element={<InvitePage />} />

                {/* Employee Routes */}
                <Route path="/timeoff" element={
                    user && role === "employee"
                        ? <EmployeeTimeOffPage user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                {/* New Employee Team Calendar and Dashboard Route */}
                <Route path="/employee/calendar" element={
                    user && role === "employee"
                        ? <TeamCalendarPage user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                {/* New Employee Settings Route */}
                <Route path="/employee/settings" element={
                    user && role === "employee"
                        ? <EmployeeSettingsPage user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                {/* Admin Routes */}
                <Route path="/admin" element={
                    user && role === "admin"
                        ? <AdminDashboard user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                <Route path="/admin/employees" element={
                    user && role === "admin"
                        ? <AdminDashboard user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                <Route path="/admin/timelogs" element={
                    user && role === "admin"
                        ? <AdminDashboard user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                <Route path="/admin/timeoff" element={
                    user && role === "admin"
                        ? <AdminTimeOffDashboard orgId={orgId} />
                        : <Navigate to="/" />
                } />

                <Route path="/admin/disputes" element={
                    user && role === "admin"
                        ? <TimeEntryDisputesDashboard orgId={orgId} />
                        : <Navigate to="/" />
                } />

                <Route path="/admin/locations" element={
                    user && role === "admin"
                        ? <AdminLocationSettingsPage user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                <Route path="/admin/settings" element={
                    user && role === "admin"
                        ? <AdminSettingsPage user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                {/* Default Route: Redirects based on auth state */}
                <Route
                    path="/"
                    element={
                        user
                            ? role === "admin"
                                ? <Navigate to="/admin" />
                                : role === "employee"
                                    ? <Navigate to="/employee" />
                                    : <Navigate to="/unassigned" />
                            : <Navigate to="/login" />
                    }
                />

                {/* Role-Based Access Routes */}
                <Route path="/admin" element={
                    user && role === "admin"
                        ? <AdminDashboard user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                <Route path="/employee" element={
                    user && role === "employee"
                        ? <EmployeeDashboard user={user} orgId={orgId} />
                        : <Navigate to="/" />
                } />

                {/* Fallback for unassigned users */}
                <Route path="/unassigned" element={
                    role === "unassigned"
                        ? <p>⚠️ You are not assigned to an organization yet.</p>
                        : <Navigate to="/" />
                } />
            </Routes>
        </Router>
    );
}

export default App;